import React, { useCallback, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence, useReducedMotion } from 'framer-motion';
import { ChevronLeftIcon, ChevronDownIcon, LoaderIcon } from 'lucide-react';
import { useFormState } from '../hooks/useFormState';
import { useLeadTracking } from '../hooks/useLeadTracking';
import { formatPhone, stripPhone } from '../utils/phoneFormat';
import { educationLevels } from '../data/degrees';
import { sanitizeTcpaHtml } from '../utils/sanitizeHtml';
import { TCPADisclosure } from './TCPADisclosure';
import { getProgramMatch, type ProgramTemplate } from '../data/programTemplates';
import { clicksSearch, leadsSearch, tcpaPost, tcpaSearch, type ContactProfile, type MMSchool, type SearchProfile, type TCPAResult } from '../services/mediaMatchers';
const startDateOptions = ['Immediately', '1–3 months', '3–6 months', '6–12 months', 'More than a year', 'Undecided'];
function getGradYearOptions(): string[] {
  const currentYear = new Date().getFullYear();
  const years: string[] = [];
  for (let y = currentYear + 2; y >= currentYear - 60; y--) years.push(String(y));
  years.push('Before 1965');
  return years;
}
const gradYearOptions = getGradYearOptions();
const STEP_LABELS = ['Program', 'Background', 'Location', 'Contact'];
interface HeroMatchFormProps {
  /** Programs offered in the Step 0 picker. */
  programs: ProgramTemplate[];
  /** Pre-selected program, when the page is about one. */
  initialProgramSlug?: string;
  /** Show each option's domain, for the all-programs index. */
  domainNameFor?: (program: ProgramTemplate) => string | undefined;
  heading?: string;
  subtext?: string;
}

/**
 * Lead form that lives directly in a program page hero, so a visitor can
 * complete the whole match without leaving the page they landed on.
 *
 * Step 0 picks the program, 1 collects background, 2 location, 3 contact. When
 * the page is about a single program, `initialProgramSlug` answers step 0 and
 * the picker is skipped entirely. Both Media Matchers Searches fire when step 2
 * completes — before the contact step renders — so the partner's own disclosure
 * is the text shown beside the consent checkbox.
 */
export function HeroMatchForm({
  programs,
  initialProgramSlug,
  domainNameFor,
  heading = 'Get Matched Free',
  subtext = 'Accredited programs. No obligation.'
}: HeroMatchFormProps) {
  const navigate = useNavigate();
  const reduceMotion = useReducedMotion();
  // A page about one program has already answered the program question, so the
  // picker is skipped rather than asking the visitor to restate it.
  const hasFixedProgram = !!initialProgramSlug;
  const firstStep = hasFixedProgram ? 1 : 0;
  const [step, setStep] = useState(firstStep);
  const [programSlug, setProgramSlug] = useState(initialProgramSlug || '');
  const [programError, setProgramError] = useState('');
  const [zipLoading, setZipLoading] = useState(false);
  const [zipLookedUp, setZipLookedUp] = useState(false);
  const {
    formData,
    errors,
    updateFormData,
    updateMultiple,
    validateUniStep1,
    validateUniStep2,
    validateUniStep3
  } = useFormState();

  // Populates the LeadiD and TrustedForm hidden inputs before anything fires.
  useLeadTracking();
  const [leads, setLeads] = useState<MMSchool[] | null>(null);
  const [tcpa, setTcpa] = useState<TCPAResult | null>(null);
  const [searchLoading, setSearchLoading] = useState(false);
  const [tcpaFailed, setTcpaFailed] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState('');
  const searchFired = useRef(false);
  const selectedProgram = programs.find(p => p.slug === programSlug);
  const buildProfile = useCallback((): SearchProfile => {
    const context = selectedProgram ? getProgramMatch(selectedProgram) : {};
    return {
      degreeInterest: context.degreeInterest || '',
      areaStudy: context.areaStudy || '',
      mProgram: context.mProgram || '',
      currentEducation: formData.currentEducation,
      gradYear: formData.gradYear,
      startTimeframe: formData.startDate,
      militaryAffiliation: formData.isMilitary,
      zipCode: formData.zipCode.replace(/\D/g, ''),
      address: formData.address,
      city: formData.city,
      state: formData.state
    };
  }, [selectedProgram, formData]);
  const runSearches = useCallback(async () => {
    setSearchLoading(true);
    setTcpaFailed(false);
    const profile = buildProfile();
    const [leadsResult, tcpaResult] = await Promise.all([leadsSearch(profile), tcpaSearch(profile)]);
    setLeads(leadsResult || []);
    setTcpa(tcpaResult);
    if (!tcpaResult) setTcpaFailed(true);
    setSearchLoading(false);
  }, [buildProfile]);
  const lookupZip = useCallback(async (zip: string) => {
    if (zip.length < 5) return;
    setZipLoading(true);
    try {
      const res = await fetch(`https://api.zippopotam.us/us/${zip}`);
      if (res.ok) {
        const data = await res.json();
        const place = data.places?.[0];
        if (place) {
          updateMultiple({
            city: place['place name'] || '',
            state: place['state abbreviation'] || ''
          });
        }
      }
    } catch {
      /* Fall through to the manual city/state fields. */
    } finally {
      setZipLookedUp(true);
      setZipLoading(false);
    }
  }, [updateMultiple]);
  const handleProgramNext = (event: React.FormEvent) => {
    event.preventDefault();
    if (!programSlug) {
      setProgramError('Please choose a program to continue.');
      return;
    }
    setStep(1);
  };
  const handleBackgroundNext = (event: React.FormEvent) => {
    event.preventDefault();
    if (validateUniStep1()) setStep(2);
  };
  const handleLocationNext = (event: React.FormEvent) => {
    event.preventDefault();
    if (!validateUniStep2()) return;
    // Last input the payload needs, so the Searches run now and the partner's
    // disclosure is in hand by the time the consent checkbox renders.
    if (!searchFired.current) {
      searchFired.current = true;
      runSearches();
    }
    setStep(3);
  };
  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    setSubmitError('');
    if (!validateUniStep3()) return;
    if (!tcpa) {
      setTcpaFailed(true);
      setSubmitError('We could not load the consent details. Please retry before submitting.');
      return;
    }
    setIsSubmitting(true);
    const profile = buildProfile();
    const contact: ContactProfile = {
      firstName: formData.firstName.trim(),
      lastName: formData.lastName.trim(),
      email: formData.email.trim(),
      phone: stripPhone(formData.phone)
    };
    await tcpaPost(tcpa.mRecordID, contact);
    const clicks = await clicksSearch(profile, contact, tcpa.mBrandID);
    setIsSubmitting(false);
    navigate('/matches', {
      state: {
        leads: leads || [],
        clicks: clicks || [],
        profile,
        contact,
        disclosure: tcpa.disclosure,
        excludeBrand: tcpa.mBrandID
      }
    });
  };
  const totalSteps = STEP_LABELS.length - firstStep;
  const currentStepNumber = step - firstStep + 1;
  const progressPercent = Math.round(currentStepNumber / totalSteps * 100);
  const selectClass = (hasError: boolean) => `block w-full pl-4 pr-10 py-3 text-sm rounded-lg border bg-white text-gray-900 appearance-none cursor-pointer transition-colors focus:outline-none focus:ring-2 ${hasError ? 'border-red-400 focus:ring-red-200' : 'border-gray-300 focus:border-navy focus:ring-navy/20'}`;
  const inputClass = (hasError: boolean) => `block w-full px-4 py-3 text-sm rounded-lg border transition-colors focus:outline-none focus:ring-2 ${hasError ? 'border-red-400 focus:ring-red-200' : 'border-gray-300 focus:border-navy focus:ring-navy/20'}`;
  const labelClass = 'block text-xs font-bold text-gray-700 mb-1.5 uppercase tracking-wider';
  const primaryButton = 'w-full h-[52px] px-8 bg-primary hover:bg-primary-hover active:bg-secondary disabled:opacity-50 disabled:pointer-events-none text-white font-semibold rounded-full text-base leading-none shadow-ds-md transition-colors duration-150 ease-out inline-flex items-center justify-center gap-2 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary';
  const backButton = 'inline-flex items-center justify-center px-4 py-3.5 rounded-lg border border-gray-300 text-sm font-semibold text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-60 transition-colors duration-200';
  const domains = domainNameFor ? Array.from(new Set(programs.map(p => domainNameFor(p) || '').filter(Boolean))) : [];
  const programOptions = domainNameFor ? domains.map(domainName => <optgroup key={domainName} label={domainName} data-id="element-307" data-mp="358hccw0gwbq7">
          {programs.filter(p => domainNameFor(p) === domainName).map(p => <option key={p.slug} value={p.slug} data-id="element-308" data-mp="otcmwdtlwifr">
                {p.name} ({p.credential})
              </option>)}
        </optgroup>) : programs.map(p => <option key={p.slug} value={p.slug} data-id="element-309" data-mp="yiw4zh9hw89k">
          {p.name} ({p.credential})
        </option>);
  const renderStep = () => {
    if (step === 0) {
      return <form onSubmit={handleProgramNext} className="space-y-5" data-id="element-310" data-mp="av5xlm2q3qrp">
          <div data-id="element-311" data-mp="5va1e909p3mp">
            <label htmlFor="hero-program" className={labelClass} data-id="element-312" data-mp="5vtad07xwhol">
              Program of Interest
            </label>
            <div className="relative" data-id="element-313" data-mp="jrevuxeysjc9">
              <select id="hero-program" value={programSlug} onChange={e => {
              setProgramSlug(e.target.value);
              if (e.target.value) setProgramError('');
            }} aria-invalid={!!programError} className={selectClass(!!programError)} data-id="element-314" data-mp="frwl2uguxon5">
                <option value="" data-id="element-315" data-mp="1jeag7ltiglbn">-- Choose a Program --</option>
                {programOptions}
              </select>
              <span className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-4 text-gray-400" data-id="element-316" data-mp="1en25mmgg2tww">
                <ChevronDownIcon className="w-4 h-4" aria-hidden="true" data-id="element-317" data-mp="19nfxgrfh43uy" />
              </span>
            </div>
            {programError && <p className="mt-1.5 text-xs text-red-600" data-id="element-318" data-mp="14n1liyar49rn">{programError}</p>}
          </div>
          <button type="submit" className={primaryButton} data-id="element-319" data-mp="zsnvzcirswuw">
            Get Started
          </button>
        </form>;
    }
    if (step === 1) {
      return <form onSubmit={handleBackgroundNext} className="space-y-4" data-id="element-320" data-mp="qr7nxbffh6tu">
          <div data-id="element-321" data-mp="2q30p7k990ycd">
            <label htmlFor="hero-education" className={labelClass} data-id="element-322" data-mp="2q1fthrb779cx">
              Highest Level of Education
            </label>
            <div className="relative" data-id="element-323" data-mp="2l1l11jbrybpr">
              <select id="hero-education" value={formData.currentEducation} onChange={e => updateFormData('currentEducation', e.target.value)} className={selectClass(!!errors.currentEducation)} data-id="element-324" data-mp="2snox7aqro7be">
                <option value="" data-id="element-325" data-mp="37ccoh4tkxuvj">-- Please Select --</option>
                {educationLevels.map(level => <option key={level} value={level} data-id="element-326" data-mp="29pmch8594wje">
                    {level}
                  </option>)}
              </select>
              <span className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-4 text-gray-400" data-id="element-327" data-mp="2jmibmn99g6ba">
                <ChevronDownIcon className="w-4 h-4" aria-hidden="true" data-id="element-328" data-mp="2nnu20uzykvkj" />
              </span>
            </div>
            {errors.currentEducation && <p className="mt-1.5 text-xs text-red-600" data-id="element-329" data-mp="3v1e1z6h9rw7o">
                {errors.currentEducation}
              </p>}
          </div>

          <div data-id="element-330" data-mp="1zxzht4qpyiic">
            <label htmlFor="hero-gradyear" className={labelClass} data-id="element-331" data-mp="1zxfzhmx5ps3s">
              High School Graduation Year
            </label>
            <div className="relative" data-id="element-332" data-mp="1uz4pnc6v3hlk">
              <select id="hero-gradyear" value={formData.gradYear} onChange={e => updateFormData('gradYear', e.target.value)} className={selectClass(!!errors.gradYear)} data-id="element-333" data-mp="28yxdmzr07jpu">
                <option value="" data-id="element-334" data-mp="1gb1gxv1fprt2">-- Please Select --</option>
                {gradYearOptions.map(year => <option key={year} value={year} data-id="element-335" data-mp="1carayidn71e7">
                    {year}
                  </option>)}
              </select>
              <span className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-4 text-gray-400" data-id="element-336" data-mp="1l92zmeuy7p40">
                <ChevronDownIcon className="w-4 h-4" aria-hidden="true" data-id="element-337" data-mp="xiyyce58g1g6" />
              </span>
            </div>
            {errors.gradYear && <p className="mt-1.5 text-xs text-red-600" data-id="element-338" data-mp="sj3599y2pufe">{errors.gradYear}</p>}
          </div>

          <div data-id="element-339" data-mp="346g63n5c9s2c">
            <label htmlFor="hero-start" className={labelClass} data-id="element-340" data-mp="38yr5mow63bud">
              When Would You Like to Start?
            </label>
            <div className="relative" data-id="element-341" data-mp="2v8nm45oo9w8m">
              <select id="hero-start" value={formData.startDate} onChange={e => updateFormData('startDate', e.target.value)} className={selectClass(!!errors.startDate)} data-id="element-342" data-mp="2v5hzqy7fh8gl">
                <option value="" data-id="element-343" data-mp="3rwty5dnxbqwx">-- Please Select --</option>
                {startDateOptions.map(opt => <option key={opt} value={opt} data-id="element-344" data-mp="3dxap8inagbjk">
                    {opt}
                  </option>)}
              </select>
              <span className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-4 text-gray-400" data-id="element-345" data-mp="3iz04kahjnbdg">
                <ChevronDownIcon className="w-4 h-4" aria-hidden="true" data-id="element-346" data-mp="27hqnfulw0vfb" />
              </span>
            </div>
            {errors.startDate && <p className="mt-1.5 text-xs text-red-600" data-id="element-347" data-mp="2mt82oq3mmw2r">{errors.startDate}</p>}
          </div>

          <div className="flex gap-3 pt-1" data-id="element-348" data-mp="2hzne1onxbeg8">
            {!hasFixedProgram && <button type="button" onClick={() => setStep(0)} className={backButton} data-id="element-349" data-mp="2e04vgzjkrfgk">
                <ChevronLeftIcon className="w-4 h-4 mr-1" aria-hidden="true" data-id="element-350" data-mp="35qj5ulsdq9nh" />
                Back
              </button>}
            <button type="submit" className={primaryButton} data-id="element-351" data-mp="31pz7xfzzf20a">
              Continue
            </button>
          </div>
        </form>;
    }
    if (step === 2) {
      return <form onSubmit={handleLocationNext} className="space-y-4" data-id="element-352" data-mp="2rrjdp45nrcmk">
          <div data-id="element-353" data-mp="1lao5s9ncaco1">
            <label htmlFor="hero-zip" className={labelClass} data-id="element-354" data-mp="1laeb48rcnhc0">
              ZIP Code
            </label>
            <input id="hero-zip" type="text" inputMode="numeric" maxLength={5} value={formData.zipCode} onChange={e => {
            const val = e.target.value.replace(/\D/g, '').slice(0, 5);
            updateFormData('zipCode', val);
            setZipLookedUp(false);
            if (val.length === 5) lookupZip(val);
          }} placeholder="12345" className={inputClass(!!errors.zipCode)} data-id="element-355" data-mp="1gb1qzvrv4od4" />
            {errors.zipCode && <p className="mt-1.5 text-xs text-red-600" data-id="element-356" data-mp="1p0ibjoqk89m2">{errors.zipCode}</p>}
            {zipLoading && <p className="mt-2 flex items-center text-xs text-gray-500" data-id="element-357" data-mp="1yvsbm6xee6x7">
                <LoaderIcon className="w-3 h-3 mr-1.5 animate-spin" aria-hidden="true" data-id="element-358" data-mp="1yuh4py8noj1v" />
                Looking up your location...
              </p>}
          </div>

          {zipLookedUp && <>
              <div className="grid grid-cols-2 gap-3" data-id="element-359" data-mp="9hnhxoma7he9">
                <div data-id="element-360" data-mp="1fwcyiv5aong">
                  <label htmlFor="hero-city" className={labelClass} data-id="element-361" data-mp="1hh9ge10shny">
                    City
                  </label>
                  <input id="hero-city" type="text" value={formData.city} onChange={e => updateFormData('city', e.target.value)} placeholder="City" className={inputClass(false)} data-id="element-362" data-mp="3sotrswdn315g" />
                </div>
                <div data-id="element-363" data-mp="fh0egxyjbcw1">
                  <label htmlFor="hero-state" className={labelClass} data-id="element-364" data-mp="fffl3bnyhzzr">
                    State
                  </label>
                  <input id="hero-state" type="text" value={formData.state} onChange={e => updateFormData('state', e.target.value)} placeholder="State" className={inputClass(false)} data-id="element-365" data-mp="u4tqk8oevx9v" />
                </div>
              </div>
              <div data-id="element-366" data-mp="1hsiaaxdblzxk">
                <label htmlFor="hero-address" className={labelClass} data-id="element-367" data-mp="1hu1qknyz8azc">
                  Street Address{' '}
                  <span className="font-normal normal-case text-gray-400" data-id="element-368" data-mp="1mrbf81ejkim4">
                    (optional)
                  </span>
                </label>
                <input id="hero-address" type="text" value={formData.address} onChange={e => updateFormData('address', e.target.value)} placeholder="123 Main St" className={inputClass(false)} data-id="element-369" data-mp="1dqnbq0mry0nh" />
              </div>
            </>}

          <div className="flex gap-3 pt-1" data-id="element-370" data-mp="pzhmiuuy1w0i">
            <button type="button" onClick={() => setStep(1)} className={backButton} data-id="element-371" data-mp="u2e90wnluc9q">
              <ChevronLeftIcon className="w-4 h-4 mr-1" aria-hidden="true" data-id="element-372" data-mp="g46eswhydp29" />
              Back
            </button>
            <button type="submit" className={primaryButton} data-id="element-373" data-mp="l4c0lw1jkhcf">
              Continue
            </button>
          </div>
        </form>;
    }
    return <form onSubmit={handleSubmit} className="space-y-4" data-id="element-374" data-mp="vmpm1oaok16l">
        <input id="leadid_token" name="leadid_token" type="hidden" data-id="element-375" data-mp="rjkj5kmotxhf" />
        <input id="xxTrustedFormCertUrl" name="xxTrustedFormCertUrl" type="hidden" data-id="element-376" data-mp="rlxbmrq2nq8q" />

        <div className="grid grid-cols-2 gap-3" data-id="element-377" data-mp="hlmzlafrwov5">
          <div data-id="element-378" data-mp="hmeqi7yfdyf3">
            <label htmlFor="hero-first" className={labelClass} data-id="element-379" data-mp="hmy113lqxkj3">
              First Name
            </label>
            <input id="hero-first" type="text" autoComplete="given-name" value={formData.firstName} onChange={e => updateFormData('firstName', e.target.value)} placeholder="Jane" className={inputClass(!!errors.firstName)} data-id="element-380" data-mp="cstswh0qv6gf" />
            {errors.firstName && <p className="mt-1.5 text-xs text-red-600" data-id="element-381" data-mp="3q1yiz4r9vct3">{errors.firstName}</p>}
          </div>
          <div data-id="element-382" data-mp="25iw6y93ke34t">
            <label htmlFor="hero-last" className={labelClass} data-id="element-383" data-mp="25li9tvfifugp">
              Last Name
            </label>
            <input id="hero-last" type="text" autoComplete="family-name" value={formData.lastName} onChange={e => updateFormData('lastName', e.target.value)} placeholder="Doe" className={inputClass(!!errors.lastName)} data-id="element-384" data-mp="2akbr7yxx45m5" />
            {errors.lastName && <p className="mt-1.5 text-xs text-red-600" data-id="element-385" data-mp="1rsiubtfqa095">{errors.lastName}</p>}
          </div>
        </div>

        <div data-id="element-386" data-mp="1vrhz9e1glx0l">
          <label htmlFor="hero-email" className={labelClass} data-id="element-387" data-mp="20kb1nv9ggt9i">
            Email Address
          </label>
          <input id="hero-email" type="email" autoComplete="email" value={formData.email} onChange={e => updateFormData('email', e.target.value)} placeholder="jane@example.com" className={inputClass(!!errors.email)} data-id="element-388" data-mp="142evs0hecqp0" />
          {errors.email && <p className="mt-1.5 text-xs text-red-600" data-id="element-389" data-mp="kgc2hakwiicm">{errors.email}</p>}
        </div>

        <div data-id="element-390" data-mp="okjvvl2b0mh6">
          <label htmlFor="hero-phone" className={labelClass} data-id="element-391" data-mp="ojs28fs9oytj">
            Phone Number
          </label>
          <input id="hero-phone" type="tel" autoComplete="tel" maxLength={13} value={formData.phone} onChange={e => updateFormData('phone', formatPhone(e.target.value))} placeholder="(555)123-4567" className={inputClass(!!errors.phone)} data-id="element-392" data-mp="thk0rsnf3j6p" />
          {errors.phone && <p className="mt-1.5 text-xs text-red-600" data-id="element-393" data-mp="alk6gb6yw3jy">{errors.phone}</p>}
        </div>

        {searchLoading ? <p className="flex items-center gap-2 text-xs text-gray-500 pt-1" data-id="element-394" data-mp="3e8kfhueg3sl8">
            <LoaderIcon className="w-3.5 h-3.5 animate-spin" aria-hidden="true" data-id="element-395" data-mp="3e6hlz0e33hbr" />
            Loading consent details...
          </p> : tcpaFailed ? <div className="rounded-lg border border-red-200 bg-red-50 p-3" data-id="element-396" data-mp="31r9qxrbmwepz">
            <p className="text-xs font-medium text-red-800" data-id="element-397" data-mp="2wqc7lbew1emw">
              We couldn't load the consent details.
            </p>
            <button type="button" onClick={() => {
          searchFired.current = true;
          runSearches();
        }} className="mt-2 inline-flex items-center rounded-md border border-red-300 bg-white px-3 py-1.5 text-xs font-bold text-red-700 hover:bg-red-100 transition-colors duration-200" data-id="element-398" data-mp="2soicdingp2u4">
              Retry
            </button>
          </div> : <TCPADisclosure checked={formData.tcpaConsent} onChange={checked => updateFormData('tcpaConsent', checked)} error={errors.tcpaConsent} checkboxClassName="mt-0.5 h-4 w-4 rounded border-gray-300 cursor-pointer flex-shrink-0 accent-[#1668E0]" data-id="element-399" data-mp="24zpom9gzu1al">
            {tcpa?.disclosure ? <span dangerouslySetInnerHTML={{
          __html: sanitizeTcpaHtml(tcpa.disclosure)
        }} data-id="element-400" data-mp="1ufdjjzw1fzlo" /> : 'By submitting this form, you are giving your express written consent to be contacted regarding educational opportunities by email, telephone, or text, including through the use of automated technology, at the number provided above. Consent is not a condition of enrollment.'}
          </TCPADisclosure>}

        {submitError && <p className="text-xs text-red-600" data-id="element-401" data-mp="1zbkt02nn7t8d">{submitError}</p>}

        <div className="flex gap-3 pt-1" data-id="element-402" data-mp="24bqdmyvhwfgy">
          <button type="button" onClick={() => setStep(2)} disabled={isSubmitting} className={backButton} data-id="element-403" data-mp="24af8ovqbd1o9">
            <ChevronLeftIcon className="w-4 h-4 mr-1" aria-hidden="true" data-id="element-404" data-mp="2wtuir34ejg6n" />
            Back
          </button>
          <button type="submit" disabled={isSubmitting} className={primaryButton} data-id="element-405" data-mp="lc97xly5i1oo">
            {isSubmitting && <LoaderIcon className="w-4 h-4 animate-spin" aria-hidden="true" data-id="element-406" data-mp="u6nmtnerra79" />}
            {isSubmitting ? 'Submitting...' : 'See My Matches'}
          </button>
        </div>
      </form>;
  };
  return <div className="bg-white rounded-2xl shadow-xl overflow-hidden ring-1 ring-gray-900/5" data-id="element-407" data-mp="2g0zyadr7cpo">
      <div className="bg-navy px-6 py-4" style={{
      backgroundColor: "#8FBEF5"
    }} data-id="element-408" data-mp="3tmuba729gs7x">
        <h2 className="text-white font-bold text-center text-sm tracking-widest uppercase" data-id="element-409" data-mp="bg66yjf1c93b">
          {heading}
        </h2>
        <p className="text-white/70 text-xs text-center mt-1" data-id="element-410" data-mp="6g1leez9so68">{subtext}</p>
      </div>

      <div className="px-6 pt-5" data-id="element-411" data-mp="1mu1o1xpi2rx6">
        <div className="flex items-center justify-between mb-1.5" data-id="element-412" data-mp="1mx5yhctk6jwa">
          <span className="text-xs font-bold uppercase tracking-wider text-navy" data-id="element-413" data-mp="1hv6sdv6fp8vn">
            Step {currentStepNumber} of {totalSteps} · {STEP_LABELS[step]}
          </span>
          <span className="text-xs text-gray-500" data-id="element-414" data-mp="2i4q02sqesq4c">{progressPercent}%</span>
        </div>
        <div className="h-1.5 w-full rounded-full bg-gray-100 overflow-hidden" role="progressbar" aria-valuenow={progressPercent} aria-valuemin={0} aria-valuemax={100} aria-label="Form progress" data-id="element-415" data-mp="2n4m3oqv72fjd">
          <motion.div className="h-full rounded-full bg-crimson" initial={false} animate={{
          width: `${progressPercent}%`
        }} transition={{
          duration: 0.25,
          ease: [0.23, 1, 0.32, 1]
        }} data-id="element-416" data-mp="2w2y57nxlmxql" />
        </div>
      </div>

      <div className="p-6" data-id="element-417" data-mp="3aslu1polg3xf">
        <AnimatePresence mode="wait" initial={false} data-id="element-418" data-mp="3avzwsz166f5e">
          <motion.div key={step} initial={{
          opacity: 0,
          y: reduceMotion ? 0 : 8
        }} animate={{
          opacity: 1,
          y: 0
        }} exit={{
          opacity: 0,
          y: reduceMotion ? 0 : -8
        }} transition={{
          duration: 0.18,
          ease: [0.23, 1, 0.32, 1]
        }} data-id="element-419" data-mp="3ftt3ruaard6q">
            {renderStep()}
          </motion.div>
        </AnimatePresence>
      </div>
    </div>;
}